/** @jsxImportSource @emotion/react */
import styled, { Interpolation } from "@emotion/styled";
import { Spacer } from "../../general/Spacer/Spacer";
import { Link } from "react-router-dom";
import { NavbarButtonOptions } from "../NavbarV2Desktop";
import { useMemo, useRef } from "react";
import { useHover } from "usehooks-ts";
import { useActive } from "hooks/useActive";
import { Theme } from "@emotion/react";
import { DeviceType, useDeviceType } from "hooks/useDeviceType";
import { GlobalStore } from "../../../store/global/GlobalStore";

export interface NavbarButtonProps extends NavbarButtonOptions {
  onClick?: () => void;
  css?: Interpolation<Theme>;
}

export const NavbarButton = ({
  symbol,
  text,
  pathName,
  colors,
  css,
  onClick,
}: NavbarButtonProps) => {
  const ref = useRef<HTMLButtonElement>(null);
  const isHover = useHover(ref);
  const isActive = useActive(ref);
  const currentPathName = window.location.pathname;
  const isSelected = currentPathName === pathName;
  const Icon = isSelected || isHover ? symbol.active : symbol.default;
  const isTablet = useDeviceType() === DeviceType.Tablet;

  const iconSize = useMemo(() => {
    if (isActive) return 26;
    if (isHover) return 30;
    return 28;
  }, [isActive, isHover]);
  return (
    <Link
      to={pathName ?? "#"}
      onClick={() => {
        GlobalStore.update(s => {
          s.modalSelector = undefined;
        });
        onClick?.();
      }}
    >
      <NavbarButtonContainer selected={isSelected} colors={colors} ref={ref} css={css}>
        <Icon width={iconSize} height={iconSize} />
        {!isTablet && (
          <>
            <Spacer width="10px" />
            {text}
          </>
        )}
      </NavbarButtonContainer>
    </Link>
  );
};

// Sub-components

interface NavbarButtonContainerProps {
  selected: boolean;
  colors: NavbarButtonOptions["colors"];
}

export const NavbarButtonContainer = styled.button<NavbarButtonContainerProps>`
  background: none;
  border: none;
  border-radius: 14px;
  width: 158px;
  height: 54px;
  text-align: left;
  display: flex;
  flex-direction: row;
  padding: 13px 16px;
  align-items: center;
  font-size: ${props => props.theme.fontSize[5]};
  color: ${props => (props.selected ? props.colors?.selected : props.theme.font.primary.default)};
  font-weight: ${props => (props.selected ? "700" : props.theme.fontWeights["work-sans-0"])};
  transition: color 0.1s linear, font-size 0.1s linear, font-weight 0.1s linear;

  &:hover {
    color: ${props => props.colors?.hover};
    font-weight: ${props => props.theme.fontWeights["work-sans-0"]};
    font-size: 14.5px;
  }

  &:active {
    color: ${props => props.colors?.active};
    font-size: 13.5px;
  }
`;
